import { useEffect, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { WrapperWidget } from '@agentselly/ui';
import { AppAction, AppRoute } from '@agentselly/immodossier-shared';
import { AppLink } from '~/components/AppLink';
import { fetchBackend } from '~/helper/fetchBackend';
import { useAppParams } from '~/helper/useAppParams';
import { useV3Redesign } from '~/helper/useV3Redesign';

interface Props {
  readonly dossierId?: string;
  readonly hidden?: boolean;
  readonly address?: {
    readonly street?: string;
    readonly streetNumber?: string;
    readonly location?: string;
  };
}

type Status = 'idle' | 'deleting' | 'deleted' | 'error';

export function DeleteDossierWidget(props: Props) {
  const dossierId = props.dossierId;
  if (props.hidden || !dossierId) {
    return null;
  }

  const { t } = useTranslate();
  const params = useAppParams();
  const v3 = useV3Redesign();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [
    isOpen,
    setIsOpen,
  ] = useState(false);
  const [
    status,
    setStatus,
  ] = useState<Status>('idle');

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) {
      return;
    }
    if (!isOpen) {
      dialog.removeAttribute('open');
      return;
    }
    // ponytail: jsdom has no showModal; real browsers get the modal dialog.
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
  }, [isOpen]);

  const confirmDelete = async () => {
    setStatus('deleting');
    try {
      const response = await fetchBackend(
        `/action/${AppAction.DeleteDossier}?dossierId=${encodeURIComponent(
          dossierId,
        )}`,
        { method: 'POST' },
      );
      setStatus(response.ok ? 'deleted' : 'error');
    } catch {
      setStatus('error');
    }
  };

  const address = [
    [
      props.address?.street,
      props.address?.streetNumber,
    ]
      .filter(Boolean)
      .join(' '),
    props.address?.location,
  ]
    .filter(Boolean)
    .join(', ');

  if (v3) {
    return (
      <div className="v3-overview">
        <section
          className="card lg fsec"
          data-od-id="edit-delete"
          aria-labelledby="delete-dossier-title"
        >
          <h2 id="delete-dossier-title">
            {t('dossier_delete_title', 'Bewertung löschen')}
          </h2>
          <p className="mu sm">
            {t(
              'dossier_delete_text',
              'Entfernt diese Bewertung samt Ihren Angaben endgültig. Das lässt sich nicht rückgängig machen.',
            )}
          </p>
          <div className="fbar">
            <button
              type="button"
              className="btn bad"
              onClick={() => {
                setStatus('idle');
                setIsOpen(true);
              }}
            >
              {t('dossier_delete_title', 'Bewertung löschen')}
            </button>
          </div>
        </section>
        <dialog
          ref={dialogRef}
          className="v3-dlg"
          aria-labelledby="delete-dossier-dialog-title"
          onClose={() => setIsOpen(false)}
        >
          <div className="dh2">
            <h2 id="delete-dossier-dialog-title">
              {t('dossier_delete_dialog_title', 'Bewertung wirklich löschen?')}
            </h2>
          </div>
          <div className="db">
            {status === 'deleted' ? (
              <div
                role="status"
                className="okbox"
              >
                <span>{t('dossier_delete_done', 'Bewertung gelöscht.')}</span>
                <AppLink
                  className="btn"
                  to={AppRoute.$LANG_$COMPANYID_$CONTACTID_INDEX}
                  params={params}
                >
                  {t('dossier_delete_back_to_overview', 'Zur Übersicht')}
                </AppLink>
              </div>
            ) : (
              <>
                <p>
                  {t(
                    'dossier_delete_dialog_text',
                    'Die Bewertung für {address} und alle Angaben werden gelöscht.',
                    { address },
                  )}
                </p>
                {status === 'error' && (
                  <p
                    role="alert"
                    className="okbox bad"
                  >
                    {t(
                      'dossier_delete_error',
                      'Löschen fehlgeschlagen. Bitte versuchen Sie es erneut.',
                    )}
                  </p>
                )}
                <div className="fbar">
                  <button
                    type="button"
                    className="btn bad"
                    disabled={status === 'deleting'}
                    onClick={confirmDelete}
                  >
                    {t('dossier_delete_confirm', 'Endgültig löschen')}
                  </button>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setIsOpen(false)}
                  >
                    {t('dossier_delete_cancel', 'Abbrechen')}
                  </button>
                </div>
              </>
            )}
          </div>
        </dialog>
      </div>
    );
  }

  return (
    <WrapperWidget>
      <section aria-labelledby="delete-dossier-title">
        <h2
          id="delete-dossier-title"
          className="text-lg font-bold"
        >
          {t('dossier_delete_title', 'Bewertung löschen')}
        </h2>
        <p className="text-sm text-gray-500 my-2">
          {t(
            'dossier_delete_text',
            'Entfernt diese Bewertung samt Ihren Angaben endgültig. Das lässt sich nicht rückgängig machen.',
          )}
        </p>
        <button
          type="button"
          className="btn btn-error btn-outline"
          onClick={() => {
            setStatus('idle');
            setIsOpen(true);
          }}
        >
          {t('dossier_delete_title', 'Bewertung löschen')}
        </button>
      </section>
      <dialog
        ref={dialogRef}
        className="rounded-xl p-6 max-w-md"
        aria-labelledby="delete-dossier-dialog-title"
        onClose={() => setIsOpen(false)}
      >
        <h2
          id="delete-dossier-dialog-title"
          className="text-lg font-bold"
        >
          {t('dossier_delete_dialog_title', 'Bewertung wirklich löschen?')}
        </h2>
        {status === 'deleted' ? (
          <div
            role="status"
            className="mt-4 flex items-center gap-4"
          >
            <span>{t('dossier_delete_done', 'Bewertung gelöscht.')}</span>
            <AppLink
              className="btn"
              to={AppRoute.$LANG_$COMPANYID_$CONTACTID_INDEX}
              params={params}
            >
              {t('dossier_delete_back_to_overview', 'Zur Übersicht')}
            </AppLink>
          </div>
        ) : (
          <>
            <p className="my-4">
              {t(
                'dossier_delete_dialog_text',
                'Die Bewertung für {address} und alle Angaben werden gelöscht.',
                { address },
              )}
            </p>
            {status === 'error' && (
              <p
                role="alert"
                className="text-error mb-4"
              >
                {t(
                  'dossier_delete_error',
                  'Löschen fehlgeschlagen. Bitte versuchen Sie es erneut.',
                )}
              </p>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                className="btn btn-error"
                disabled={status === 'deleting'}
                onClick={confirmDelete}
              >
                {t('dossier_delete_confirm', 'Endgültig löschen')}
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => setIsOpen(false)}
              >
                {t('dossier_delete_cancel', 'Abbrechen')}
              </button>
            </div>
          </>
        )}
      </dialog>
    </WrapperWidget>
  );
}
